import React, { createContext, useState, useContext, useEffect } from 'react';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoadingAuth, setIsLoadingAuth] = useState(true);
  const [isLoadingPublicSettings, setIsLoadingPublicSettings] = useState(false);
  const [authError, setAuthError] = useState(null);
  const [appPublicSettings, setAppPublicSettings] = useState(null);
  const [gcalToken, setGcalToken] = useState(null); // 👈 added

  useEffect(() => {
    checkAppState();
  }, []);

  const checkAppState = async () => {
    setIsLoadingAuth(true);
    setAuthError(null);

    const token = localStorage.getItem("token");
    if (!token) {
      setAuthError({ type: 'auth_required', message: 'Authentication required' });
      setIsAuthenticated(false);
      setIsLoadingAuth(false);
      return;
    }

    try {
      const res = await fetch("/api/me", {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();

      if (!res.ok || data.error) {
        localStorage.removeItem("token");
        setAuthError({ type: 'auth_required', message: 'Authentication required' });
        setIsAuthenticated(false);
      } else {
        setUser(data);
        setIsAuthenticated(true);
        // 👇 store google token in context if connected
        if (data.google_calendar?.connected) {
          setGcalToken({
            access_token: data.google_calendar.access_token,
            expires_at: data.google_calendar.expires_at,
          });
        }
      }
    } catch (err) {
      console.error("Auth check failed:", err);
      setAuthError({ type: 'unknown', message: err.message || 'Failed to load app' });
      setIsAuthenticated(false);
    }

    setIsLoadingAuth(false);
  };

  // 👇 call this before any Google Calendar write operation
  const getGcalAccessToken = async () => {
    const BUFFER_MS = 5 * 60 * 1000;

    // Still valid — return immediately
    if (gcalToken?.access_token && Date.now() < gcalToken.expires_at - BUFFER_MS) {
      return gcalToken.access_token;
    }

    // Expired — hit the refresh worker, update context
    const token = localStorage.getItem("token");
    const res = await fetch("/api/gcalendarrefresh", {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
    });

    if (!res.ok) {
      const { error } = await res.json();
      throw new Error(error || "Google Calendar token expired. Please reconnect.");
    }

    const { access_token, expires_at } = await res.json();
    setGcalToken({ access_token, expires_at });
    return access_token;
  };

  const logout = (shouldRedirect = true) => {
    const token = localStorage.getItem("token");
    fetch("/api/logout", {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
    }).catch(() => {});

    localStorage.removeItem("token");
    localStorage.removeItem("user");
    setUser(null);
    setIsAuthenticated(false);
    setGcalToken(null); // 👈 clear on logout

    if (shouldRedirect) window.location.href = "/login";
  };

  const navigateToLogin = () => { window.location.href = "/login"; };

  return (
    <AuthContext.Provider value={{
      user,
      isAuthenticated,
      isLoadingAuth,
      isLoadingPublicSettings,
      authError,
      appPublicSettings,
      gcalToken,         // 👈 exposed
      getGcalAccessToken, // 👈 exposed
      logout,
      navigateToLogin,
      checkAppState,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};